import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTimes, faTrash, faUser, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import ConfirmModal from '../../../components/ConfirmModal';
import { usuarioExtensaoService } from '../../../services/usuarioExtensaoService';

const UsuarioExtensaoView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const loadAccess = useCallback(async () => {
    try {
      setLoading(true);
      const response = await usuarioExtensaoService.getById(id);
      
      if (response.success && response.data) {
        setAccess(response.data);
      } else {
        setError('Acesso não encontrado');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  const handleDelete = async () => {
    try {
      await usuarioExtensaoService.delete(id);
      setShowDeleteModal(false);
      navigate('/seguranca/usuarios/extensoes');
    } catch (err) {
      setError(err.message || 'Erro ao excluir acesso');
      setShowDeleteModal(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Não definida';
    const date = new Date(dateString);
    const adjustedDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    return adjustedDate.toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="w-full bg-white rounded shadow p-6 mt-6">
        <div className="text-center py-8">
          <div className="text-gray-500">Carregando...</div>
        </div>
      </div>
    );
  }

  if (error && !access) {
    return (
      <div className="w-full bg-white rounded shadow p-6 mt-6">
        <div className="text-center py-8">
          <div className="text-red-600 mb-4">{error}</div>
          <button 
            onClick={() => navigate('/seguranca/usuarios/extensoes')}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white rounded shadow p-6 mt-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold">Detalhes do Acesso Usuário-Extensão</h2>
        <div className="flex gap-2">
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Informações do Usuário */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <FontAwesomeIcon icon={faUser} className="text-blue-600" />
            Informações do Usuário
          </h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-600">Nome</label>
              <div className="text-gray-900">{access?.usuario?.nome || 'N/A'}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600">Email</label>
              <div className="text-gray-900">{access?.usuario?.email || 'N/A'}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600">ID do Usuário</label>
              <div className="text-gray-900">#{access?.usuario_id}</div>
            </div>
          </div>
        </div>

        {/* Informações da Extensão */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <FontAwesomeIcon icon={faBuilding} className="text-green-600" />
            Informações da Extensão
          </h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-600">Nome</label>
              <div className="text-gray-900">{access?.extensao?.nome || 'N/A'}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600">Localização</label>
              <div className="text-gray-900">
                {access?.extensao?.cidade && access?.extensao?.uf 
                  ? `${access.extensao.cidade}/${access.extensao.uf}` 
                  : 'N/A'}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600">ID da Extensão</label>
              <div className="text-gray-900">#{access?.extensao_id}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600">Status da Extensão</label>
              <div className="text-gray-900">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  access?.extensao?.status === 'Ativa' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {access?.extensao?.status}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detalhes do Acesso */}
      <div className="mt-6 bg-gray-50 rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-4">Detalhes do Acesso</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-600">ID do Acesso</label>
            <div className="text-gray-900">#{access?.id}</div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600">Status do Acesso</label>
            <div className="text-gray-900">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                access?.ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {access?.ativo ? 'Ativo' : 'Inativo'}
              </span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600">Data de Início</label>
            <div className="text-gray-900">{formatDate(access?.data_inicio)}</div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600">Data de Fim</label>
            <div className="text-gray-900">{formatDate(access?.data_fim)}</div>
          </div>
        </div>
        
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-600">Data de Criação</label>
          <div className="text-gray-900">{formatDate(access?.criado_em)}</div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-600">Criado por</label>
          <div className="text-gray-900">{access?.criado_por_nome || 'N/A'}</div>
        </div>

        {access?.observacoes && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-600">Observações</label>
            <div className="text-gray-900 bg-white p-3 rounded border">
              {access.observacoes}
            </div>
          </div>
        )}
      </div>

      {/* Botões de Ação */}
      <div className="flex flex-row gap-1 justify-end mt-6 w-full overflow-x-auto">
        <button 
          type="button" 
          className="p-2 sm:px-4 sm:py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center gap-2 w-10 sm:w-auto justify-center" 
          onClick={() => navigate('/seguranca/usuarios/extensoes')}
        >
          <FontAwesomeIcon icon={faTimes} />
          <span className="hidden sm:inline">Cancelar</span>
        </button>
          <button 
            onClick={() => setShowDeleteModal(true)}
            className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faTrash} />
            <span className="hidden sm:inline">Excluir</span>
          </button>        
          <button 
            onClick={() => navigate(`/seguranca/usuarios/extensoes/${id}/edit`)}
            className="px-3 py-2 bg-green-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faEdit} />
            <span className="hidden sm:inline">Editar</span>
          </button>
      </div>

      <ConfirmModal
        isOpen={showDeleteModal}
        title="Confirmar Exclusão"
        message="Deseja realmente excluir este acesso? Esta ação não pode ser desfeita."
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        confirmText="Confirmar"
        cancelText="Cancelar"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        danger
      />
    </div>
  );
};

export default UsuarioExtensaoView;
